import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus, Lock, Mail, User, Building2, ArrowLeft, KeyRound } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { TwoFactorVerification } from './TwoFactorVerification';
import { useTwoFactorAuth } from '../hooks/useTwoFactorAuth';

export function AuthPage({ onBackToLanding }: { onBackToLanding?: () => void }) {
  const navigate = useNavigate();
  const { user, signIn, signUp } = useAuth();
  const { checkDeviceIsTrusted, registerTrustedDevice } = useTwoFactorAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [checking2FA, setChecking2FA] = useState(false);

  useEffect(() => {
    if (user && !requires2FA && !checking2FA) {
      navigate('/app', { replace: true });
    }
  }, [user, requires2FA, checking2FA, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (isSignUp) {
      if (!firstName || !lastName || !company) {
        setError('Veuillez remplir tous les champs');
        return;
      }

      if (password !== confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }

      if (password.length < 8) {
        setError('Le mot de passe doit contenir au moins 8 caractères');
        return;
      }

      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);

      if (!hasUpperCase || !hasLowerCase || !hasNumber) {
        setError('Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre');
        return;
      }

      setLoading(true);

      const { error } = await signUp(email, password, firstName, lastName, company);
      if (error) {
        setError(error.message);
      } else {
        setSuccess('Compte créé avec succès ! Vous pouvez maintenant vous connecter.');
        setIsSignUp(false);
        setPassword('');
        setConfirmPassword('');
        setFirstName('');
        setLastName('');
        setCompany('');
      }

      setLoading(false);
      return;
    }

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      setError('Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre');
      return;
    }

    setLoading(true);
    setChecking2FA(true);

    const { error } = await signIn(email, password);
    if (error) {
      setError('Email ou mot de passe incorrect');
      setLoading(false);
      setChecking2FA(false);
      return;
    }

    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (currentUser) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('two_factor_enabled')
        .eq('id', currentUser.id)
        .single();

      if (profile?.two_factor_enabled) {
        const isTrusted = await checkDeviceIsTrusted(currentUser.id);

        if (!isTrusted) {
          setPendingEmail(email);
          setRequires2FA(true);
          setChecking2FA(false);

          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-2fa-code`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${session.access_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ip_address: null }),
              }
            );
          }
        } else {
          setChecking2FA(false);
        }
      } else {
        setChecking2FA(false);
      }
    } else {
      setChecking2FA(false);
    }

    setLoading(false);
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email) {
      setError('Veuillez entrer votre adresse email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-password-reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      setLoading(false);

      if (!response.ok) {
        setError('Une erreur est survenue. Veuillez réessayer.');
        console.error('Reset password error:', data);
      } else {
        setSuccess(
          'Un email de réinitialisation a été envoyé à votre adresse. Vérifiez votre boîte de réception (et vos spams).'
        );
        setTimeout(() => {
          setIsForgotPassword(false);
          setSuccess('');
        }, 5000);
      }
    } catch (error) {
      setLoading(false);
      setError('Une erreur est survenue. Veuillez réessayer.');
      console.error('Reset password error:', error);
    }
  }

  const handle2FAVerified = async (rememberDevice: boolean) => {
    if (rememberDevice) {
      await registerTrustedDevice(30);
    }
    setRequires2FA(false);
    setChecking2FA(false);
    navigate('/app', { replace: true });
  };

  const handle2FACancel = async () => {
    await supabase.auth.signOut();
    setRequires2FA(false);
    setPendingEmail('');
    setChecking2FA(false);
  };

  if (requires2FA) {
    return (
      <TwoFactorVerification
        email={pendingEmail}
        onVerified={handle2FAVerified}
        onCancel={handle2FACancel}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {onBackToLanding && (
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={onBackToLanding}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Retour au site</span>
            </button>
          </div>
        )}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/20 p-3 rounded-lg">
              {isForgotPassword ? (
                <KeyRound size={32} />
              ) : isSignUp ? (
                <UserPlus size={32} />
              ) : (
                <LogIn size={32} />
              )}
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2">
            {isForgotPassword
              ? 'Mot de passe oublié'
              : isSignUp
              ? 'Créer un compte'
              : 'Connexion'}
          </h1>
          <p className="text-blue-100 text-center text-sm">
            {isForgotPassword
              ? 'Réinitialisez votre mot de passe'
              : isSignUp
              ? 'Essai gratuit 14 jours — sans carte bancaire'
              : 'Bienvenue sur votre espace de gestion'}
          </p>
        </div>

        <form onSubmit={isForgotPassword ? handleForgotPassword : handleSubmit} className="p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {success}
            </div>
          )}

          <div className="space-y-4">
            {isForgotPassword ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail size={16} className="inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="votre@email.com"
                  required
                />
                <p className="mt-2 text-sm text-gray-600">
                  Entrez votre adresse email pour recevoir un lien de réinitialisation
                </p>
              </div>
            ) : isSignUp ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User size={16} className="inline mr-2" />
                      Prénom *
                    </label>
                    <input
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Jean"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User size={16} className="inline mr-2" />
                      Nom *
                    </label>
                    <input
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Dupont"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 size={16} className="inline mr-2" />
                    Entreprise / Association *
                  </label>
                  <input
                    type="text"
                    required
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom de votre structure"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail size={16} className="inline mr-2" />
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock size={16} className="inline mr-2" />
                    Mot de passe *
                  </label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    8 caractères minimum, une majuscule, une minuscule, un chiffre
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock size={16} className="inline mr-2" />
                    Confirmer le mot de passe *
                  </label>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail size={16} className="inline mr-2" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    placeholder="votre@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock size={16} className="inline mr-2" />
                    Mot de passe
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        setIsForgotPassword(true);
                        setError('');
                        setSuccess('');
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? 'Chargement...'
              : isForgotPassword
              ? 'Envoyer le lien de réinitialisation'
              : isSignUp
              ? 'Créer mon compte gratuit'
              : 'Se connecter'}
          </button>

          <div className="mt-6 text-center space-y-3">
            {isForgotPassword ? (
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setError('');
                  setSuccess('');
                }}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
              >
                Retour à la connexion
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                  setSuccess('');
                }}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
              >
                {isSignUp
                  ? 'Déjà un compte ? Se connecter'
                  : 'Pas encore de compte ? Essai gratuit 14 jours'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
